import { Component, computed, effect, EventEmitter, HostListener, inject, Output, signal } from '@angular/core';
import { Router } from '@angular/router';
import { AuthStore } from '../../../core/store/authentication/authentication.store';
import { injectDispatch } from '@ngrx/signals/events';
import { authApiEvents } from '../../../core/store/authentication/authentication.events';
import { ProviderStore } from '../../../core/store/provider/provider.store';
import { FeeScheduleStore } from '../../../core/store/fee-schedule/fee-schedule.store';
import { ClinicStore } from '../../../core/store/clinic/clinic.store';
import { ClinicAdminStore } from '../../../core/store/clinic/clinic-admin.store';
import { ClinicLocationStore } from '../../../core/store/clinic/clinic-location.store';
import { MemberStore } from '../../../core/store/member/member.store';
import { AuthService } from '../../../core/services/auth';
import { CurrentAdminService } from '../../../core/services/admin/current-admin.service';
import { take } from 'rxjs';

@Component({
    standalone: true,
    selector: 'app-navbar',
    imports: [],
    templateUrl: './navbar.component.html',
    styleUrl: './navbar.component.scss'
})
export class NavbarComponent {
  store = inject(AuthStore)
  private readonly authService = inject(AuthService)
  private readonly currentAdminService = inject(CurrentAdminService)
  clinicStore = inject(ClinicStore)
  clinicAdminStore = inject(ClinicAdminStore)
  clinicLocationStore = inject(ClinicLocationStore)
  providerStore = inject(ProviderStore)
  feeScheduleStore = inject(FeeScheduleStore)
  memberStore = inject(MemberStore)
  readonly dispatch = injectDispatch(authApiEvents)
  private readonly adminDisplayName = signal('')
  private adminIdentityLoaded = ''
  readonly headerVisible = signal(true)
  @Output() readonly headerVisibilityChange = new EventEmitter<boolean>()

  readonly userDisplayName = computed(() => {
    // Track authentication changes so the name is recalculated after login/hydration.
    this.store.isAuthenticated()

    const token = this.authService.getDecodedToken() ?? {}
    const tokenFullName = String(token.name ?? '').trim()
    const tokenParts = [token.given_name, token.family_name]
      .map(value => String(value ?? '').trim())
      .filter(Boolean)
    const isAdmin = this.isAdminToken(token)

    return this.adminDisplayName() ||
      tokenFullName ||
      tokenParts.join(' ') ||
      String(this.store.name() ?? '').trim() ||
      String(this.store.first_name() ?? '').trim() ||
      String(isAdmin
        ? token.email ?? ''
        : token['cognito:username'] ?? token.email ?? this.store.username() ?? ''
      ).trim()
  })

  constructor() {
    effect(() => {
      if (!this.store.isAuthenticated()) {
        this.adminDisplayName.set('')
        this.adminIdentityLoaded = ''
        return
      }

      const token = this.authService.getDecodedToken() ?? {}
      if (!this.isAdminToken(token)) return

      const identity = String(token.sub ?? token['cognito:username'] ?? token.email ?? 'admin')
      if (this.adminIdentityLoaded === identity) return

      this.adminIdentityLoaded = identity
      this.currentAdminService.getCurrentAdmin().pipe(take(1)).subscribe({
        next: admin => {
          if (this.adminIdentityLoaded !== identity) return

          const fullName = String(admin.name ?? '').trim() ||
            [admin.first_name, admin.last_name]
              .map(value => String(value ?? '').trim())
              .filter(Boolean)
              .join(' ')

          this.adminDisplayName.set(fullName || String(admin.email_address ?? admin.email ?? '').trim())
        },
        error: () => {
          // Keep the token name/email fallback when the admin profile cannot be loaded.
        }
      })
    })
  }

  private isAdminToken(token: Record<string, any>): boolean {
    const groups = Array.isArray(token['cognito:groups'])
      ? token['cognito:groups']
      : [token['cognito:groups']]

    return groups.some(group => String(group ?? '').toLowerCase().includes('admin'))
  }

  @HostListener('window:scroll')
  onWindowScroll(): void {
    this.setHeaderVisibility(window.scrollY <= 4)
  }

  showHeader(): void {
    this.setHeaderVisibility(true)
  }

  hideHeaderAfterPointerLeaves(): void {
    if (window.scrollY > 4) {
      this.setHeaderVisibility(false)
    }
  }

  private setHeaderVisibility(visible: boolean): void {
    if (this.headerVisible() === visible) return

    this.headerVisible.set(visible)
    this.headerVisibilityChange.emit(visible)
  }

  $loggedIn !: boolean

readonly router = inject(Router);

  logout() {
    this.store.clear()
    this.clinicStore.clear()
    this.clinicAdminStore.clear()
    this.clinicLocationStore.clear()
    this.providerStore.clear()
    this.feeScheduleStore.clear()
    this.memberStore.clear()
    this.dispatch.logout(true)
  }
}
